import { useProjects, type Project } from './useProjects'
import { useAgenda, type AgendaEvent } from './useAgenda'
import { useFormations } from './useFormations'
import { useDateFormat } from './useDateFormat'

export interface Stat {
    label: string
    value: number
    suffix: string
    icon: string
}

export interface Testimonial {
    id: string
    author: string
    role: string
    content: string
    authorLink?: string
    image?: string
    imageAlt?: string
    authorType: 'Student' | 'Alumni' | 'Speaker' | 'Company'
    orderIndex: number
    isActive: boolean
}

export interface ReviewStat {
    id: string
    reviewYearId: string
    label: string
    value: number
    maxValue: number
    orderIndex: number
    icon?: string
}

export interface ReviewYear {
    id: string
    year: number
    studentCount: number
    isActive: boolean
    globalSatisfaction: number
    stats: ReviewStat[]
}

export interface HomeFormation {
    title: string
    description: string
    icon: string
    link: string
    image: string
    classes: {
        icon: string
        borderHover: string
        iconBgHover: string
        titleHover: string
        buttonHover: string
    }
    isRncp?: boolean
}

export interface Partner {
    id: string
    name: string
    firstName?: string
    type: string
    image: string
    imageAlt?: string
    backgroundImage?: string
    website?: string
    priority: number
    joinedDate?: string
    socials?: { platform: string; url: string; icon: string }[]
    logoFormat?: 'square' | 'rectangular'
    jobs?: string[]
    companies?: string[]
    locations?: string[]
}

export interface HeroConfig {
    buttonText: string
    buttonUrl: string
    buttonVisible: boolean
    imageVertical?: string
    imageHorizontal?: string
    imageAlt?: string
}

export interface HeroUpdate {
    type: 'news' | 'event'
    title: string
    date: string // ISO Date
    displayDate: string // Formatted 05 Janv.
    day: string // 05
    month: string // Janv.
    category: string
    slug: string
    image: string
}

export const useHome = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase


    // Fetch Stats from API
    const getStats = async (): Promise<Stat[]> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/school/stats`)
            if (error.value || !data.value) {
                console.error('Error fetching stats:', error.value)
                return []
            }
            return (data.value as Stat[]).map(s => ({
                ...s,
                icon: s.icon.startsWith('fa-') ? s.icon : `fa-${s.icon}`
            }))
        } catch (e) {
            console.error('Error fetching stats:', e)
            return []
        }
    }

    const getHeroConfig = async (): Promise<HeroConfig> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/school/hero`)
            if (error.value || !data.value) {
                // Fallback default
                return {
                    buttonText: '',
                    buttonUrl: '',
                    buttonVisible: false
                }
            }
            const config = data.value as HeroConfig
            return {
                ...config
            }
        } catch (e) {
            console.error('Error fetching hero config:', e)
            return {
                buttonText: '',
                buttonUrl: '',
                buttonVisible: false
            }
        }
    }

    // Fetch Formations from API
    const getFormations = async (): Promise<HomeFormation[]> => {
        const { getAllFormations } = useFormations()
        const formations = await getAllFormations()

        return formations.map(f => {
            let color = f.themeColor || 'oxo-red'
            // Add oxo- prefix if it's a known branding color and prefix is missing
            const brandingColors = ['red', 'yellow', 'purple', 'rose', 'pink', 'fuchsia', 'indigo', 'blue', 'sky', 'cyan', 'teal', 'emerald', 'green', 'lime', 'amber', 'orange', 'darkblue', 'black', 'white', 'grey', 'dark', 'card', 'light', 'text', 'success']
            if (brandingColors.includes(color)) {
                color = `oxo-${color}`
            }

            const icon = f.iconName || 'fa-gamepad'

            return {
                ...f,
                icon: icon,
                link: `/formations/${f.slug}`,
                image: f.image,
                classes: {
                    icon: `text-${color}`,
                    borderHover: `hover:border-${color}/30 dark:hover:border-${color}/30`,
                    iconBgHover: `group-hover:bg-${color}/10`,
                    titleHover: `group-hover:text-${color}`,
                    buttonHover: `group-hover:bg-${color} group-hover:border-${color} ${color === 'oxo-yellow' ? 'group-hover:text-slate-900' : ''}`
                }
            }
        })
    }

    // Fetch Partners from API
    const getPartners = async (): Promise<Partner[]> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/partners`)
            if (error.value) {
                console.error('Error fetching partners:', error.value)
                return []
            }
            return (data.value as Partner[]) || []
        } catch (e) {
            console.error('Error fetching partners:', e)
            return []
        }
    }

    // Fetch Projects from API (General) - Top 10 for Home Section
    const getProjects = async (): Promise<Project[]> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/projects`)
            if (error.value) {
                console.error('Error fetching projects:', error.value)
                return []
            }

            const all = (data.value as Project[]) || []

            // Sort by DisplayOrder FIRST
            const sorted = all.sort((a, b) => {
                const orderA = a.displayOrder ?? 9999
                const orderB = b.displayOrder ?? 9999
                if (orderA !== orderB) return orderA - orderB
                // Fallback to Year Desc
                const dateA = new Date(a.realizationDate || a.createdAt || 0).getTime()
                const dateB = new Date(b.realizationDate || b.createdAt || 0).getTime()
                return dateB - dateA
            })

            return sorted.slice(0, 10)
        } catch (e) {
            console.error('Error fetching projects:', e)
            return []
        }
    }

    // Fetch Hero Projects (Top 5 by DisplayOrder)
    const getHeroProjects = async (): Promise<Project[]> => {
        const { getAllProjects } = useProjects()
        const all = await getAllProjects()

        // Filter valid projects (published)
        // Sort by DisplayOrder Ascending (0 is highest priority/first)
        // If DisplayOrder is equal (e.g. 0), fallback to Year Descending to keep freshness
        const sorted = all
            .filter(p => p.isPublishedOnWebsite)
            .sort((a, b) => {
                const orderA = a.displayOrder ?? 9999
                const orderB = b.displayOrder ?? 9999
                if (orderA !== orderB) return orderA - orderB

                // Fallback to recent date
                const dateA = new Date(a.realizationDate || a.createdAt || 0).getTime()
                const dateB = new Date(b.realizationDate || b.createdAt || 0).getTime()
                return dateB - dateA
            })

        return sorted.slice(0, 3)
    }

    // Fetch Latest News from API
    const getLatestNews = async (): Promise<any> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/news`)
            if (error.value) {
                console.error('Error fetching news:', error.value)
                return null
            }
            const response = data.value as any
            const all = response?.items || response?.Items || []
            const news = all.length > 0 ? all[0] : null
            if (news) {
                return news
            }
            return null
        } catch (e) {
            console.error('Error fetching news:', e)
            return null
        }
    }

    // Fetch Reviews (Years & Stats) from API
    const getReviews = async (): Promise<ReviewYear[]> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/qualiopi/satisfaction`)
            if (error.value) {
                console.error('Error fetching reviews:', error.value)
                return []
            }
            return (data.value as ReviewYear[]) || []
        } catch (e) {
            console.error('Error fetching reviews:', e)
            return []
        }
    }

    // Fetch Testimonials from API
    const getTestimonials = async (): Promise<Testimonial[]> => {
        try {
            const { data, error } = await useFetch(`${apiBase}/oxoweb/testimonials`)
            if (error.value) {
                console.error('Error fetching testimonials:', error.value)
                return []
            }
            return (data.value as Testimonial[]) || []
        } catch (e) {
            console.error('Error fetching testimonials:', e)
            return []
        }
    }

    // Fetch Agenda from API
    const getAgenda = async (): Promise<AgendaEvent[]> => {
        const { getUpcomingEvents } = useAgenda()
        const allEvents = await getUpcomingEvents()
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        return allEvents
            .filter(event => {
                const end = event.dateEndRaw ? new Date(event.dateEndRaw) : new Date(event.dateRaw)
                return end >= today
            })
            .slice(0, 3)
    }

    // Combined fetch for Hero (News OR Agenda)
    const getLatestUpdate = async (): Promise<HeroUpdate[]> => {
        // Fetch both in parallel
        const [news, events] = await Promise.all([
            getLatestNews(),
            getAgenda()
        ])

        const candidates: HeroUpdate[] = []

        // SSR-safe: use deterministic date formatting
        const { getDateParts } = useDateFormat()
        const formatDate = (dateStr: string) => getDateParts(dateStr)

        // Process News
        if (news) {
            const fmt = formatDate(news.date)
            candidates.push({
                type: 'news',
                title: news.title,
                date: news.date,
                displayDate: fmt.displayDate,
                day: fmt.day,
                month: fmt.month,
                category: news.category || 'Actualité',
                slug: news.slug,
                image: news.image
            })
        }

        // Process Events (Agenda) - Get the ONE nearest upcoming event
        if (events && events.length > 0) {
            // events are already filtered >= today and sorted by date via API/useAgenda
            const nextEvent = events[0]
            if (nextEvent) {
                const start = new Date(nextEvent.dateRaw)
                const end = nextEvent.dateEndRaw ? new Date(nextEvent.dateEndRaw) : start
                const now = new Date()
                let calcDate = now
                if (calcDate < start) calcDate = start
                if (calcDate > end) calcDate = end

                const fmt = formatDate(calcDate.toISOString())

                candidates.push({
                    type: 'event',
                    title: nextEvent.title,
                    date: calcDate.toISOString(), // Use effective date for sorting
                    displayDate: fmt.displayDate,
                    day: fmt.day,
                    month: fmt.month,
                    category: nextEvent.type, // e.g. JPO, Masterclass
                    slug: nextEvent.slug,
                    image: nextEvent.image
                })
            }
        }

        // Sort candidates by Date Descending
        candidates.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

        return candidates
    }

    return {
        getStats,
        getFormations,
        getPartners,
        getProjects,
        getReviews,
        getTestimonials,
        getAgenda,
        getHeroConfig,
        getLatestNews,
        getLatestUpdate,
        getHeroProjects
    }
}
